// CabinetSlots — ÉCRAN 5 de la maquette : réservation d'un créneau (v5.20.0).
// Avatar+titre · Visio/Sur place · dates · grille horaire 3 col · durée 15 min · Confirmer.
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { T, PhotoAvatar } from "./cabinetTheme";

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const MONTHS = ["jan", "fév", "mar", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"];

function nextDays(n = 7) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(Date.now() + i * 86400000);
    out.push({
      date: d.toISOString().slice(0, 10),
      top: i === 0 ? "Aujourd'hui" : i === 1 ? "Demain" : DAYS[d.getDay()],
      bottom: `${DAYS[d.getDay()]} ${d.getDate()}${i >= 2 ? ` ${MONTHS[d.getMonth()]}` : ""}`,
    });
  }
  return out;
}

export const CabinetSlots = ({ slug, member, qualification, source, onBooked }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const days = nextDays();
  const [date, setDate] = useState(days[0].date);
  const [mode, setMode] = useState("visio");
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSlots(null); setTime(null);
    axios.get(`${API}/api/v3/cabinet/${slug}/members/${member.member_id}/slots`, { params: { date } })
      .then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [slug, member.member_id, date]);

  const confirm = async () => {
    const token = localStorage.getItem("civ_user_token");
    if (!token) {
      toast.info("Connectez-vous pour confirmer votre rendez-vous");
      navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/v3/cabinet/${slug}/appointments`,
        { member_id: member.member_id, date, time, mode, qualification, source },
        { headers: { Authorization: `Bearer ${token}` } });
      onBooked(r.data.appointment);
    } catch (e) {
      if (e.response?.status === 401) { navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`); return; }
      toast.error(e.response?.data?.message || "Réservation impossible — réessayez");
    } finally { setSaving(false); }
  };

  return (
    <div style={{ display: "grid", gap: 12 }} data-testid="cabinet-slots">
      <div style={{ display: "flex", gap: 11, alignItems: "center" }}>
        <PhotoAvatar member={member} size={50} />
        <div>
          <div style={{ fontSize: 15, fontWeight: 900, lineHeight: 1.25 }}>Prendre rendez-vous<br />avec {member.name}</div>
          <div style={{ ...T.sub, fontSize: 12 }}>{member.title}{qualification?.domain ? ` · ${qualification.domain}` : ""}</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        {[["visio", "📹 Visio"], ["cabinet", "Sur place"]].map(([v, l]) => (
          <button key={v} onClick={() => setMode(v)} data-testid={`slot-mode-${v}`}
            style={{
              flex: 1, padding: "9px 0", borderRadius: 10, fontSize: 12.5, fontWeight: 800, cursor: "pointer",
              ...(mode === v
                ? { background: "#ECFDF5", border: "1.5px solid #0E8A4F", color: "#0E8A4F" }
                : { background: "#F9FAFB", border: "1px solid #E5E7EB", color: "#6B7280" }),
            }}>{l}</button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 7, overflowX: "auto", paddingBottom: 2, scrollbarWidth: "none" }} data-testid="slot-dates">
        {days.map((d) => (
          <button key={d.date} onClick={() => setDate(d.date)} data-testid={`slot-date-${d.date}`}
            style={{
              minWidth: 74, padding: "8px 6px", borderRadius: 11, cursor: "pointer", textAlign: "center",
              ...(date === d.date
                ? { background: "#065F46", border: "1px solid #065F46", color: "#fff" }
                : { background: "#fff", border: "1px solid #E5E7EB", color: "#111827" }),
            }}>
            <div style={{ fontSize: 11.5, fontWeight: 900 }}>{d.top}</div>
            <div style={{ fontSize: 10.5, opacity: 0.75, marginTop: 1 }}>{d.bottom}</div>
          </button>
        ))}
      </div>

      {slots === null ? (
        <div style={T.sub}>Chargement des créneaux…</div>
      ) : slots.length === 0 ? (
        <div style={{ ...T.card, ...T.sub }} data-testid="slots-empty">Aucun créneau ce jour — essayez une autre date.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }} data-testid="slot-grid">
          {slots.map((s) => (
            <button key={s.time} disabled={!s.available} onClick={() => setTime(s.time)} data-testid={`slot-time-${s.time}`}
              style={{
                padding: "10px 0", borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: s.available ? "pointer" : "not-allowed",
                border: time === s.time ? "1px solid #0E8A4F" : "1px solid #E5E7EB",
                background: time === s.time ? "#0E8A4F" : s.available ? "#fff" : "#F3F4F6",
                color: time === s.time ? "#fff" : s.available ? "#111827" : "#C4CCC7",
              }}>{s.time}</button>
          ))}
        </div>
      )}

      <div style={{ ...T.card, display: "flex", gap: 10, alignItems: "center", padding: "11px 14px" }}>
        <span style={{ fontSize: 17 }}>🗓</span>
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 800 }}>Durée : 15 min</div>
          <div style={{ ...T.sub, fontSize: 11.5 }}>Consultation d'orientation · <span style={{ color: "#0E8A4F", fontWeight: 800 }}>Gratuit</span></div>
        </div>
      </div>

      <button onClick={confirm} disabled={!time || saving} data-testid="slot-confirm"
        style={{ ...T.btnGreen, width: "100%", padding: "13px 0", opacity: !time || saving ? 0.5 : 1 }}>
        {saving ? "Confirmation…" : "Confirmer le rendez-vous"}
      </button>
    </div>
  );
};
